import { CLAUDE_GUIDE_URL } from '../lib/links';

/**
 * What's new, one entry per release. The newest entry comes first; its
 * version is what users are considered to have "seen" once they close the
 * panel. Fill this in the merge request that ships the feature. Before a
 * production release, compose the lot the entry puts forward: see « Nouveautés :
 * composer le lot » in documentation/dev-workflow.md.
 */
/**
 * `key` lets the feature flag itself as new where it lives (see `useNewFeature`).
 * `recallOf` marks a short reminder of an item from an earlier release, taken into a newer
 * lot: it is left out wherever that earlier release is shown too (the full history, or the
 * panel of someone who missed both), so the same announcement never appears twice.
 */
export type ReleaseItem = { key?: string; title: string; text: string; href?: string; recallOf?: string };
export type Release = { version: string; date: string; title: string; items: ReleaseItem[] };

export const releases: Release[] = [
  {
    version: '1.8.0',
    date: '2026-10',
    // The lot of 1.8.0: the Saisie rebuilt around the cell (#27), the live rate (#54), the kept
    // drafts (#55), the guided tour, the colour scale (#46) and the Claude views by client (#43).
    // No recall: 1.7.0 came out a few days before and the panel shows it anyway to whoever has
    // not closed it yet.
    title: 'Une Saisie plus rapide, et des couleurs plus justes',
    items: [
      {
        title: "Staffer jusqu'à une date, en un geste",
        text: "Sur une case de la Saisie, la flèche → répète la valeur jusqu'à la fin du projet, une date ou un nombre de semaines, même au-delà des semaines affichées. C'est enregistré tout de suite, et « Annuler » reste possible.",
        href: '/saisie-staffing',
      },
      {
        title: 'Comme dans Excel',
        text: "Tirez le petit carré d'une case vers la droite pour recopier sa valeur sur quelques semaines. Le menu ⋯ de la ligne et la date de fin du projet, désormais cliquable, font le reste : remplir, vider, prolonger.",
        href: '/saisie-staffing',
      },
      {
        title: 'Le taux en direct',
        text: "Pendant que vous saisissez, un panneau montre le taux de staffing du consultant sur les mois concernés, avant et après vos modifications. Une surcharge s'affiche en jours.",
        href: '/saisie-staffing',
      },
      {
        title: 'Rien ne se perd',
        text: "Vos modifications non enregistrées sont gardées si vous changez de page. Au retour, l'app vous signale les cases qu'un collègue a modifiées entre-temps, et vous choisissez.",
        href: '/saisie-staffing',
      },
      {
        title: 'Une visite guidée',
        text: 'La première fois que vous ouvrez la Saisie, une courte visite montre ces gestes. Vous la revoyez à tout moment avec « Comment ça marche ? ».',
        href: '/saisie-staffing',
      },
      {
        title: 'Des couleurs plus justes',
        text: "Le vert commence maintenant à 70 % : un consultant dans la moyenne d'Equancy ne s'affiche plus en orange. Le jaune marque 50 à 69 %, l'orange moins de 50 %, le rouge 0 %, et la légende sous le tableau reprend l'échelle.",
        href: '/',
      },
      {
        title: 'Le staffing par client dans Claude',
        text: 'Dans Claude, demandez qui travaille pour un client ou sur un projet, mois par mois ou semaine par semaine, et filtrez les stagiaires ou les consultants en remote.',
        href: CLAUDE_GUIDE_URL,
      },
    ],
  },
  {
    version: '1.7.0',
    date: '2026-10',
    // The lot of 1.7.0: its own features, plus « Saisie à plusieurs » from 1.6.0 (released the
    // day before, and the same subject as the presence).
    title: 'Voir qui saisit en même temps que vous, et des filtres toujours à portée de main',
    items: [
      {
        title: 'Qui saisit en ce moment',
        text: "Quand un collègue saisit le planning d'un consultant en même temps que vous, ses initiales apparaissent sur la ligne, dans la Saisie et sur le tableau de staffing. Dans la Saisie, la case qu'il modifie est entourée de sa couleur.",
        href: '/saisie-staffing',
      },
      {
        title: 'Qui a modifié une case',
        text: "Au survol d'une case de la Saisie, vous voyez qui l'a modifiée en dernier et quand. Pour les saisies plus anciennes, seule la date s'affiche.",
        href: '/saisie-staffing',
      },
      {
        title: 'Les filtres vous suivent',
        text: "Dans une longue grille de saisie, les filtres (équipes, recherche) restent en haut de l'écran, comme sur le tableau de staffing. Plus besoin de remonter pour changer d'équipe.",
        href: '/saisie-staffing',
      },
      {
        recallOf: '1.6.0',
        title: 'Saisie à plusieurs',
        text: "Si deux personnes modifient quand même la même case, l'app n'écrase rien. Elle montre les deux valeurs, et vous choisissez.",
        href: '/saisie-staffing',
      },
    ],
  },
  {
    version: '1.6.0',
    date: '2026-10',
    // The lot of 1.6.0: its own features, plus the two 1.5.0 announcements worth a second
    // look (released the day before, many managers had not opened the app since).
    title: "Une application plus rapide, des saisies qui ne s'écrasent plus, et le staffing dans Claude",
    items: [
      {
        title: 'Plus rapide',
        text: "Le tableau de staffing s'affiche en une fraction de seconde au lieu de plusieurs : les taux se calculent bien plus vite, et une recherche ou un dépliage ne recharge plus rien. L'app reste aussi éveillée entre deux visites : plus de longue attente à la première ouverture.",
        href: '/',
      },
      {
        title: 'Saisie à plusieurs',
        text: "Si quelqu'un a modifié la même case pendant que vous saisissiez, l'app ne l'écrase plus : elle vous montre sa valeur et la vôtre, et vous choisissez laquelle garder.",
        href: '/saisie-staffing',
      },
      {
        title: 'Début et fin de projet',
        text: 'La semaine où un projet commence ou se termine se saisit normalement. La case indique combien de jours restent disponibles : 3 pour un projet qui démarre un mercredi.',
        href: '/saisie-staffing',
      },
      {
        title: 'Recherche plus souple',
        text: "Plus besoin des accents ni de l'orthographe exacte : « leo » trouve « Léo », « jean pierre » trouve « Jean-Pierre », « de la fontaine » trouve « Delafontaine », et les graphies courantes d'un prénom se rejoignent (Mohamed et Mohammed, Hicham et Hisham). Valable dans toutes les recherches de l'app, et quand vous demandez un nom à Claude.",
        href: '/',
      },
      {
        key: 'claude-guide',
        recallOf: '1.5.0',
        title: 'Managers : le staffing dans Claude',
        text: "Qui est disponible en novembre, le taux d'une équipe, le planning d'un consultant : posez la question à Claude, qui répond avec vos droits dans l'app. Le plugin est déjà installé, et « Utiliser dans Claude », dans le menu de gauche, mène à la page de prise en main.",
        href: CLAUDE_GUIDE_URL,
      },
      {
        key: 'report-problem',
        recallOf: '1.5.0',
        title: 'Signaler un souci',
        text: "Un bug, une idée, quelque chose qui ne marche pas comme prévu ? Le bouton « Signaler un souci », dans le menu de gauche, prépare un message avec le contexte : il ne reste qu'à décrire le problème et à l'envoyer.",
      },
    ],
  },
  {
    version: '1.5.0',
    date: '2026-10',
    title: 'Un souci se signale en un clic, et le staffing arrive dans Claude',
    items: [
      {
        key: 'report-problem',
        title: 'Signaler un souci',
        text: "Quelque chose ne marche pas, ou pas comme prévu ? Le bouton « Signaler un souci », dans le menu de gauche, prépare un message avec le contexte (la page, la version) : décrivez le problème en deux lignes, puis copiez-le dans Teams ou un e-mail, ou envoyez-le par e-mail à l'équipe de l'app.",
      },
      {
        title: 'Managers : le staffing dans Claude',
        text: "Qui est disponible en novembre, le taux d'occupation d'une équipe, le planning d'un consultant : posez la question à Claude (claude.ai, Cowork ou Claude Code), qui répond avec vos droits dans l'app. Le plugin « equancy-staffing » est déjà installé dans Claude, et la page de prise en main montre comment s'en servir. Réservé pour l'instant aux managers, et en lecture seule : rien ne se modifie depuis Claude.",
        href: CLAUDE_GUIDE_URL,
      },
    ],
  },
  {
    version: '1.4.0',
    date: '2026-10',
    title: 'Le bloc-notes sous la main',
    items: [
      {
        key: 'notebooks',
        title: 'Le bloc-notes partagé, plus visible',
        text: "Dispos du moment, questions à trancher, opportunités : le bloc-notes de l'équipe staffing existait mais se trouvait mal. Il s'ouvre maintenant de deux endroits, le bouton « Bloc-notes » en haut du tableau et la languette bleue sur le côté droit.",
        href: '/',
      },
      {
        key: 'quick-create',
        title: 'Créer un client ou un projet sans quitter la saisie',
        text: "Dans « Ajouter un projet », un lien « Nouveau client » ou « Nouveau projet » crée ce qui manque sur place et le sélectionne. Même chose pour le client dans le formulaire de projet.",
        href: '/saisie-staffing',
      },
      {
        title: 'Les filtres du tableau vous suivent',
        text: "Sur le Dashboard, dès que le bloc de filtres sort de l'écran, une barre compacte reste en haut avec les mêmes réglages : équipes, période, recherche, disponibilité, stagiaires. « Haut » ramène au bloc complet.",
        href: '/',
      },
      {
        title: 'Les nouveautés sont signalées là où elles sont',
        text: "Une pastille « NEW » marque pendant quelques semaines les fonctionnalités récentes ; son survol dit laquelle et mène à ce panneau.",
      },
    ],
  },
  {
    version: '1.3.0',
    date: '2026-10',
    title: 'Trouver qui est disponible',
    items: [
      {
        key: 'availability-filter',
        title: 'Filtre de disponibilité sur le tableau',
        text: "Masquez les consultants staffés au-dessus d'un seuil (50, 70, 80 ou 90 %) sur l'horizon de votre choix : le mois en cours, les 2 prochains mois (par défaut) ou plus.",
        href: '/',
      },
      {
        key: 'extend-month',
        title: 'Prolonger un projet depuis la saisie',
        text: "Quand la date de fin d'un projet est atteinte, un bouton « +1 mois » à côté de ses dates la repousse d'un mois et rouvre les cellules de saisie, sans passer par la fiche projet.",
        href: '/saisie-staffing',
      },
      {
        key: 'remote-province',
        title: 'Les consultants en remote disent d\'où',
        text: 'La fiche consultant a une case « En remote » avec la province (ou la ville). Elle apparaît dans la liste des consultants et, par une épingle, sur le tableau de staffing ; elle se cherche comme un nom.',
        href: '/consultants',
      },
    ],
  },
  {
    version: '1.2.0',
    date: '2026-10',
    title: 'Vos filtres vous suivent, et vous êtes au courant des nouveautés',
    items: [
      {
        title: 'Vos filtres sont mémorisés',
        text: "Équipes, période, recherche, stagiaires : l'application retrouve vos filtres d'une connexion à l'autre, sur n'importe quel poste.",
        href: '/staffing',
      },
      {
        title: 'Vos équipes présélectionnées',
        text: "À la première connexion, le tableau et la saisie s'ouvrent directement sur les équipes dont vous êtes manager.",
        href: '/saisie-staffing',
      },
      {
        title: 'Ce panneau « Nouveautés »',
        text: 'Il s\'ouvre une fois à chaque nouvelle version, et reste accessible depuis le bouton « Nouveautés » en bas du menu.',
      },
    ],
  },
];

export const LATEST_VERSION = releases[0].version;

/** Semver-like comparison of 'X.Y.Z' strings: negative when a < b. */
export function compareVersions(a: string, b: string): number {
  const pa = a.split('.').map(Number);
  const pb = b.split('.').map(Number);
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
    const d = (pa[i] || 0) - (pb[i] || 0);
    if (d !== 0) return d;
  }
  return 0;
}

/**
 * Releases the user has not seen yet, newest first: everything after `seenVersion`, or
 * everything when they never closed the panel. Production receives several releases at
 * once, so the first panel after a deployment must list them all, not just the latest.
 */
export function releasesSince(seenVersion: string | null | undefined): Release[] {
  if (!seenVersion) return releases;
  return releases.filter((r) => compareVersions(r.version, seenVersion) > 0);
}

/** The items of `release` to show in a panel listing `shown`: reminders of a release that is listed too are left out. */
export function itemsToShow(release: Release, shown: Release[]): ReleaseItem[] {
  const versions = new Set(shown.map((r) => r.version));
  return release.items.filter((item) => !item.recallOf || !versions.has(item.recallOf));
}

/** The release and item behind a feature key, or null when no entry carries it. */
export function findReleaseItem(key: string): { release: Release; item: ReleaseItem } | null {
  for (const release of releases) {
    const item = release.items.find((i) => i.key === key);
    if (item) return { release, item };
  }
  return null;
}
